import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../../Constants';
import BuildingLookupForm from './BuildingLookupForm';
import BuildingInspectionTable from '../TestsInspectionsEntry/InspectionsList';

const BuildingInspectionsEntryPage = () => {
  // 1) We now store buildingId + agencyId
  const [buildingId, setBuildingId] = useState('');
  const [agencyId, setAgencyId] = useState('');

  // The array of existing inspections for the table
  const [inspections, setInspections] = useState([]);

  // Data for the inspection form
  const [devices, setDevices] = useState([]);
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [agencyTests, setAgencyTests] = useState([]);

  // Form fields
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [managementCompanyId, setManagementCompanyId] = useState('');
  const [testId, setTestId] = useState('');
  const [taskType, setTaskType] = useState('perform');
  const [approvalDate, setApprovalDate] = useState('');
  const [inspYear, setInspYear] = useState('');
  const [hours, setHours] = useState('');
  const [servicer, setServicer] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [lastDate, setLastDate] = useState('');

  const [canSubmit, setCanSubmit] = useState(false);
  const [error, setError] = useState(false);

  // ------------------------------------------
  // A) If buildingId changes => fetch inspections
  // ------------------------------------------
  useEffect(() => {
    if (!buildingId) {
      setInspections([]);
      return;
    }
    const fetchInspections = async () => {
      try {
        const url = `${API_BASE_URL}/api/inspections/building/${buildingId}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP error! ${res.status}`);
        const data = await res.json();
        const withFlags = (data || []).map((row) => ({ ...row, isEditing: false }));
        setInspections(withFlags);
      } catch (err) {
        console.error('Error fetching inspections:', err);
        setInspections([]);
      }
    };
    fetchInspections();
  }, [buildingId]);

  // ------------------------------------------
  // B) If buildingId changes => fetch devices
  // ------------------------------------------
  useEffect(() => {
    if (!buildingId) {
      setDevices([]);
      return;
    }
    const fetchDevices = async () => {
      try {
        const url = `${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP error! ${res.status}`);
        const data = await res.json();
        setDevices(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching devices:', err);
        setDevices([]);
      }
    };
    fetchDevices();
  }, [buildingId]);

  // ------------------------------------------
  // C) If agencyId changes => fetch agency tests
  // ------------------------------------------
  useEffect(() => {
    if (!agencyId) {
      setAgencyTests([]);
      setTestId('');
      return;
    }
    const fetchAgencyTests = async () => {
      try {
        const url = `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${agencyId}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP error! ${res.status}`);
        const data = await res.json();
        setAgencyTests(Array.isArray(data) ? data : []);
        setTestId('');
      } catch (err) {
        console.error('Error fetching agency tests:', err);
        setAgencyTests([]);
      }
    };
    fetchAgencyTests();
  }, [agencyId]);

  // ------------------------------------------
  // D) Fetch mgmt companies once
  // ------------------------------------------
  useEffect(() => {
    const fetchMgmtCos = async () => {
      try {
        const url = `${API_BASE_URL}/api/setup/managementcompanies/all`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP error! ${res.status}`);
        const data = await res.json();
        setManagementCompanies(data || []);
      } catch (err) {
        console.error('Error fetching mgmt companies:', err);
        setManagementCompanies([]);
      }
    };
    fetchMgmtCos();
  }, []);

  // ------------------------------------------
  // E) Validate required fields => canSubmit
  // ------------------------------------------
  useEffect(() => {
    const hasDevices = selectedDeviceIds.length > 0;
    const required = [
      buildingId,
      hasDevices,
      managementCompanyId,
      testId,
      taskType,
      approvalDate,
      inspYear,
    ];
    const allFilled = required.every((val) => !!val && val !== 0 && val !== false);
    setCanSubmit(allFilled);
    if (allFilled) setError(false);
  }, [buildingId, selectedDeviceIds, managementCompanyId, testId, taskType, approvalDate, inspYear]);

  // ------------------------------------------
  // F) Create => POST
  // ------------------------------------------
  const handleSubmitInspection = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setError(true);
      return;
    }
    const payload = {
      buildingId: Number(buildingId),
      deviceIds: selectedDeviceIds.map(Number),
      managementCompanyId: Number(managementCompanyId),
      testId: testId ? Number(testId) : null,
      taskType,
      approvalDate,
      inspYear: parseInt(inspYear, 10) || 0,
      hours: parseInt(hours, 10) || 0,
      servicer,
      nextDate,
      lastDate,
      status: 'Active',
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/inspections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(`HTTP error! ${res.status}`);
      }
      alert('Inspection created successfully!');

      // Refresh
      const getRes = await fetch(`${API_BASE_URL}/api/inspections/building/${buildingId}`, {
        credentials: 'include',
      });
      if (!getRes.ok) {
        throw new Error(`HTTP error! ${getRes.status}`);
      }
      const updatedData = await getRes.json();
      const withFlags = (updatedData || []).map((row) => ({ ...row, isEditing: false }));
      setInspections(withFlags);

      // Clear form
      setSelectedDeviceIds([]);
      setManagementCompanyId('');
      setTestId('');
      setTaskType('perform');
      setApprovalDate('');
      setInspYear('');
      setHours('');
      setServicer('');
      setNextDate('');
      setLastDate('');
      setError(false);
    } catch (err) {
      console.error('Error creating inspection:', err);
      alert(`Error: ${err.message}`);
    }
  };

  // ------------------------------------------
  // G) Table => remove
  // ------------------------------------------
  const handleRemoveInspection = async (inspectionId) => {
    try {
      const url = `${API_BASE_URL}/api/inspections/${inspectionId}`;
      const delRes = await fetch(url, { method: 'DELETE', credentials: 'include' });
      if (!delRes.ok) {
        throw new Error(`HTTP error! ${delRes.status}`);
      }
      alert('Inspection removed!');
      setInspections((prev) => prev.filter((i) => i.inspectionId !== inspectionId));
    } catch (err) {
      console.error('Error removing inspection:', err);
      alert(`Could not remove inspection: ${err.message}`);
    }
  };

  // ------------------------------------------
  // H) Table => update
  // ------------------------------------------
  const handleUpdateInspection = async (inspectionId, updatedObj) => {
    try {
      const url = `${API_BASE_URL}/api/inspections/${inspectionId}`;
      const putRes = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatedObj),
      });
      if (!putRes.ok) {
        throw new Error(`HTTP error! ${putRes.status}`);
      }
      const data = await putRes.json();
      alert('Inspection updated successfully!');

      setInspections((prev) =>
        prev.map((insp) =>
          insp.inspectionId === inspectionId ? { ...insp, ...data, isEditing: false } : insp
        )
      );
    } catch (err) {
      console.error('Error updating inspection:', err);
      alert(`Could not update inspection: ${err.message}`);
    }
  };

  // handle device check/uncheck
  const handleDeviceCheckbox = (devId, checked) => {
    setSelectedDeviceIds((prev) => {
      const devStr = String(devId);
      return checked ? [...prev, devStr] : prev.filter((id) => id !== devStr);
    });
  };

  // Handler for clearing everything
  const handleClear = () => {
    setBuildingId('');
    setAgencyId('');
    setInspections([]);
    setDevices([]);
    setAgencyTests([]);
    setSelectedDeviceIds([]);
    setManagementCompanyId('');
    setTestId('');
    setTaskType('perform');
    setApprovalDate('');
    setInspYear('');
    setHours('');
    setServicer('');
    setNextDate('');
    setLastDate('');
    setError(false);
    setCanSubmit(false);
  };

  // The function the BuildingLookupForm calls => set building + agency
  const handleBuildingAndAgencyPicked = (bId, aId) => {
    setBuildingId(String(bId));
    setAgencyId(String(aId));
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* TOP SECTION in two columns */}
      <div className="flex p-4 border-b space-x-4">
        {/* LEFT COLUMN => Building Lookup */}
        <div className="w-1/2 border-r pr-4">
          <BuildingLookupForm
            buildingId={buildingId}
            onBuildingChange={handleBuildingAndAgencyPicked}
          />
          <button
            type="button"
            onClick={handleClear}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Clear
          </button>
        </div>

        {/* RIGHT COLUMN => Create Inspection Form */}
        <div className="w-1/2 pl-4">
          <h2 className="text-xl font-bold mb-2">Inspections Entry</h2>

          <form onSubmit={handleSubmitInspection} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Management Company */}
              <div>
                <label className="block font-semibold mb-1">Management Company</label>
                <select
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={managementCompanyId}
                  onChange={(e) => setManagementCompanyId(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {managementCompanies.map((mc) => (
                    <option key={mc.managementId} value={mc.managementId}>
                      {mc.companyName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Agency Test */}
              <div>
                <label className="block font-semibold mb-1">Agency Test</label>
                <select
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={testId}
                  onChange={(e) => setTestId(e.target.value)}
                >
                  <option value="">-- Select Test --</option>
                  {agencyTests.map((t) => (
                    <option key={t.testId} value={t.testId}>
                      {t.label || `Test #${t.testId}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Task Type */}
              <div>
                <label className="block font-semibold mb-1">Task Type</label>
                <select
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                >
                  <option value="perform">Perform</option>
                  <option value="witness">Witness</option>
                  <option value="both">Both</option>
                </select>
              </div>

              {/* Approval Date */}
              <div>
                <label className="block font-semibold mb-1">Approval Date</label>
                <input
                  type="date"
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={approvalDate}
                  onChange={(e) => setApprovalDate(e.target.value)}
                />
              </div>

              {/* Insp Year */}
              <div>
                <label className="block font-semibold mb-1">Inspection Year</label>
                <input
                  type="number"
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={inspYear}
                  onChange={(e) => setInspYear(e.target.value)}
                />
              </div>

              {/* Hours */}
              <div>
                <label className="block font-semibold mb-1">Hours</label>
                <input
                  type="number"
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>

              {/* Servicer */}
              <div>
                <label className="block font-semibold mb-1">Servicer</label>
                <input
                  type="text"
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={servicer}
                  onChange={(e) => setServicer(e.target.value)}
                />
              </div>

              {/* Next Date */}
              <div>
                <label className="block font-semibold mb-1">Next Date</label>
                <input
                  type="date"
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={nextDate}
                  onChange={(e) => setNextDate(e.target.value)}
                />
              </div>

              {/* Last Date */}
              <div>
                <label className="block font-semibold mb-1">Last Date</label>
                <input
                  type="date"
                  className="border border-gray-300 w-full p-1 rounded-md"
                  value={lastDate}
                  onChange={(e) => setLastDate(e.target.value)}
                />
              </div>
            </div>

            {/* Device checkboxes */}
            <div className="mt-4">
              <label className="block font-semibold mb-1">Select Device(s)</label>
              <div className="border border-gray-300 rounded-md p-2 max-h-32 overflow-auto">
                {devices.length === 0 ? (
                  <p className="text-sm text-gray-600">No devices found for this building.</p>
                ) : (
                  devices.map((dev) => {
                    const checked = selectedDeviceIds.includes(String(dev.deviceId));
                    return (
                      <label key={dev.deviceId} className="flex items-center space-x-2 mb-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setSelectedDeviceIds((prev) =>
                              e.target.checked
                                ? [...prev, String(dev.deviceId)]
                                : prev.filter((id) => id !== String(dev.deviceId))
                            )
                          }
                        />
                        <span>{`#${dev.deviceId} - ${dev.deviceType || 'Unknown'}`}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Create Button */}
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={!canSubmit}
                className={`px-4 py-2 rounded ${
                  canSubmit
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                }`}
              >
                Create
              </button>
            </div>

            {error && (
              <p className="text-red-600 text-sm mt-2">
                Please fill out all required fields (Building, at least one device, Mgmt Co,
                Agency Test, Task Type, Approval Date, Insp Year).
              </p>
            )}
          </form>
        </div>
      </div>

      {/* BOTTOM => Inspections Table */}
      <div className="p-4">
        <h2 className="text-xl font-bold mb-2">Inspections Table</h2>
        <BuildingInspectionTable
          assignedInspections={inspections}
          setAssignedInspections={setInspections}
          onUpdateInspection={handleUpdateInspection}
          onRemoveInspection={handleRemoveInspection}
          isEditMode={true}
        />
      </div>
    </div>
  );
};

export default BuildingInspectionsEntryPage;
